import type { LiteralUnion } from "type-fest";

export const partners = [
    "aafes",
    "adt",
    "adtbdpp",
    "amazon",
    "amazonrouter",
    "armstrongmccall",
    "asurion",
    "asurionhomeplus",
    "asuriontechcare",
    "att",
    "attconnectedhome",
    "attnac",
    "backmarket",
    "bjs",
    "blains",
    "bluestream",
    "brightspeed",
    "cactus",
    "cgx",
    "clyde",
    "comcastbusiness",
    "cosmoprof",
    "cox",
    "cricket",
    "dell",
    "dish",
    "dsg",
    "ee",
    "expertuniversity",
    "fieldstream",
    "fingerhut",
    "fios",
    "frontier",
    "gettington",
    "golfgalaxy",
    "guitarcenter",
    "homepro",
    "hotwire",
    "hotwirecommunications",
    "hpsales",
    "jcp",
    "koodo",
    "leakprotect",
    "lgelectronics",
    "liberty",
    "life360",
    "mcafeesecurityscan",
    "mcafeetechsupport",
    "mcx",
    "mediacom",
    "meijer",
    "microsoft",
    "nex",
    "nexcom",
    "nrg",
    "optimum",
    "optimumbiz",
    "others",
    "overstock",
    "paycheckdirect",
    "pilot",
    "quill",
    "reach",
    "sallybeauty",
    "samsung",
    "samsungna",
    "shopko",
    "smartpay",
    "soluto",
    "sparklight",
    "sparklightsales",
    "sprint",
    "staples",
    "stapleshome",
    "staplessmb",
    "telus",
    "telushomepro",
    "thesource",
    "tmobile",
    "tracfone",
    "tsc",
    "unknown",
    "uscellular",
    "vcs",
    "verizon",
    "verizonconnectedhome",
    "verizonwholeoffice",
    "viasat",
    "vip",
    "vzwsmb",
    "xboxcareplus",
    "yahoo",
] as const;
/** known partner or any string */
export type Partner = LiteralUnion<(typeof partners)[number], string>;

const knownPartners: ReadonlySet<string> = new Set(partners);

/**
 * Whether `value` is one of {@link partners}. `Partner` accepts any string, so the type alone can't
 * stop a raw Connect/CSD code (e.g. `pvl_the_source`) reaching APIs that validate partner against
 * an enum and 400 on anything else.
 */
export function isKnownPartner(value: string | undefined): boolean {
    return value !== undefined && knownPartners.has(value);
}

/**
 * Converts a partner value from UI display text to the canonical partner **key** expected
 * by APIs and backends: lowercase, with spaces and `&` removed (e.g. how "AT&T" is shown
 * to users vs. the `att` slug sent in requests).
 *
 * @param partner - Label or mixed-case string from the UI (e.g. `"AT&T"`)
 *   or any string to normalize toward the backend form.
 * @returns The normalized key, e.g. `"AT&T"` → `"att"`, `"Verizon"` → `"verizon"`.
 *
 * @example
 * normalizePartnerKeyForApi("AT&T"); // "att"
 */
export function normalizePartnerKeyForApi(partner: string): string {
    return partner.toLowerCase().replace(/&/g, "").replace(/ /g, "");
}

const SIMPLR_ELIGIBLE_PARTNERS = new Set(["staples", "stapleshome", "staplessmb", "verizon"]);

/**
 * Returns whether `partner` is eligible for a feature whose production rollout is restricted to
 * the partners Simplr targets. In production this requires `partner` to normalize (via
 * {@link normalizePartnerKeyForApi}) to one of {@link SIMPLR_ELIGIBLE_PARTNERS} (currently
 * `staples`, `stapleshome`, `staplessmb` and `verizon`); outside production,
 * any non-empty partner is eligible. Used as a client-side backstop for gradual Simplr cutovers (e.g.
 * GAIA -> Simplr) — extend {@link SIMPLR_ELIGIBLE_PARTNERS} as the target list grows.
 *
 * @param partner - Partner key or UI display label (e.g. `"Staples Home"`), or `undefined`/empty
 *   when no partner is known yet.
 * @param isProdMode - Whether this is a production build.
 * @returns `true` if the partner is eligible for the cutover, given the environment.
 *
 * @example
 * isPartnerEligibleForSimplrCutover("Staples Home", true); // true
 * isPartnerEligibleForSimplrCutover("verizon", true); // true
 * isPartnerEligibleForSimplrCutover("verizon", false); // true
 * isPartnerEligibleForSimplrCutover(undefined, false); // false
 */
export function isPartnerEligibleForSimplrCutover(partner: string | undefined, isProdMode: boolean): boolean {
    if (!partner) {
        return false;
    }
    if (!isProdMode) {
        return true;
    }
    return SIMPLR_ELIGIBLE_PARTNERS.has(normalizePartnerKeyForApi(partner));
}
